import moment from 'moment-timezone';
import Pet from '@spike/pet-model';
import { Order, AppointmentOrderItem, AddOnServiceOrderItem } from '@spike/order-model';
import { Task } from '@spike/task-model';

/**
 * View model used by the Booking Detail print preview.
 * Data is grouped by pet and then by calendar day.
 */
export interface PrintBookingDetailViewModel {
    pet: Pet;
    rawAppointments: AppointmentOrderItem[];
    rawAddOns: AddOnServiceOrderItem[];
    groups: PrintBookingDetailGroupViewModel[];
}

export interface PrintBookingDetailGroupViewModel {
    dateKey: string; // YYYY-MM-DD
    dateLabel: string;
    appointments: PrintBookingDetailAppointmentViewModel[];
    addOns: PrintBookingDetailAddOnViewModel[];
}

export interface PrintBookingDetailAppointmentViewModel {
    id: string;
    businessAreaId?: string;
    businessAreaName?: string;
    serviceName?: string;
    staffName?: string;
    timeLabel?: string; // Non-boarding
    rangeLabel?: string; // Boarding
    fromLabel?: string; // Boarding
    toLabel?: string; // Boarding
    nightsCount?: number; // Boarding
    raw: AppointmentOrderItem;
    // Optional, populated for daycare/boarding appointments if tasks contain feeding/medication
    feedingSchedules?: Array<any>;
    medications?: Array<any>;
    groupedMedications?: GroupedTasks;
}

export interface PrintBookingDetailAddOnViewModel {
    id: string;
    timeLabel: string;
    name: string;
    staffName?: string;
    raw: AddOnServiceOrderItem;
}

interface GroupedMed {
    name: string;
    instructions: string;
    times: string[];
}

interface GroupedTasks {
    [dateKey: string]: {
        [medName: string]: GroupedMed;
    };
}

/* ---------------- Helpers ---------------- */

const primaryService = (item: AppointmentOrderItem) => item.appointment?.services?.[0];

const appointmentFrom = (item: AppointmentOrderItem) => item.appointment.duration.from;
const appointmentTo = (item: AppointmentOrderItem) => item.appointment.duration.to;

const addOnStart = (item: AddOnServiceOrderItem) => item.orderItem.startedAt as unknown as moment.Moment;

const sortByMomentAsc = (a: moment.Moment, b: moment.Moment) => a.valueOf() - b.valueOf();

const formatTime = (m: moment.Moment, tz: string) => m.clone().tz(tz).format('h:mm a');

const formatDateTime = (m: moment.Moment, tz: string) => m.clone().tz(tz).format('ddd, DD MMM YYYY · h:mm a');

const dayKeyFromMoment = (m: moment.Moment, tz: string) => m.clone().tz(tz).format('YYYY-MM-DD');

const dayLabelFromKey = (dateKey: string, tz: string) =>
    moment.tz(dateKey, 'YYYY-MM-DD', tz).format('dddd, DD MMM YYYY');

const computeNights = (from: moment.Moment, to: moment.Moment) =>
    Math.max(0, to.clone().startOf('day').diff(from.clone().startOf('day'), 'days'));

const buildBoardingRangeLabel = (from: moment.Moment, to: moment.Moment, nights: number, tz: string) => {
    return `${formatDateTime(from, tz)} – ${formatDateTime(to, tz)} · ${nights} Night${nights === 1 ? '' : 's'}`;
};

const getGroupedMedicationsFromTasks = (medicationTasks: Task[], tz: string) => {
    const grouped: GroupedTasks = {};

    medicationTasks.forEach(task => {
        if (!task.taskDate) return;

        const dateKey = dayKeyFromMoment(task.taskDate, tz);
        const medName = task.medication?.medicationName || 'Unknown Medication';

        if (!grouped[dateKey]) {
            grouped[dateKey] = {};
        }

        if (!grouped[dateKey][medName]) {
            grouped[dateKey][medName] = {
                name: medName,
                instructions: task.medication?.medicationInstructions || '',
                times: []
            };
        }

        const time = formatTime(task.taskDate, tz);
        if (!grouped[dateKey][medName].times.includes(time)) {
            grouped[dateKey][medName].times.push(time);
        }
    });

    Object.values(grouped).forEach(dayMeds => {
        Object.values(dayMeds).forEach(med => {
            med.times.sort((a, b) => moment.tz(a, 'h:mm a', tz).diff(moment.tz(b, 'h:mm a', tz)));
        });
    });

    return grouped;
};

const mapAppointment = (item: AppointmentOrderItem, tz: string): PrintBookingDetailAppointmentViewModel => {
    const svc = primaryService(item);
    const staff = svc?.staff;

    const businessAreaId = svc?.businessArea?.id;
    const from = appointmentFrom(item);
    const to = appointmentTo(item);

    const isBoarding = businessAreaId === 'boarding';

    const nightsCount =
        typeof item.appointment.nightsCount === 'number'
            ? item.appointment.nightsCount
            : isBoarding
            ? computeNights(from, to)
            : undefined;

    // Map feeding schedules and medications from tasks for daycare/boarding
    const tasks = item.appointment?.tasks ?? [];

    // Feedings: group similar feeding tasks
    const feedingTasks = tasks.filter(task => task.feeding?.feedingMomentTypeId);
    const feedingGrouped = new Map<string, any[]>();
    feedingTasks.forEach(task => {
        const key = `${task.feeding?.foodTypeId || ''}-${task.feeding?.foodQuantity || ''}-${
            task.feeding?.foodQuantityUnitId || ''
        }-${task.feeding?.feedingInstructions || ''}`;
        if (!feedingGrouped.has(key)) feedingGrouped.set(key, []);
        feedingGrouped.get(key)!.push(task);
    });

    const feedingSchedules = Array.from(feedingGrouped.values()).map(group => {
        const first = group[0];
        const momentTypes = Array.from(new Set(group.map((t: any) => t.feeding?.feedingMomentTypeId).filter(Boolean)));
        const time = first.taskDate ? first.taskDate.format('h:mmA') : undefined;
        const label = [first.feedingBrand ?? first.feeding?.feedingBrand, first.feeding?.foodTypeId]
            .filter(Boolean)
            .join(' · ');
        return {
            momentTypes,
            time,
            label: label || undefined,
            // preserve original instructions
            details: first.feeding?.feedingInstructions || undefined,
            feedingInstructions: first.feeding?.feedingInstructions || undefined,
            // include structured fields to resolve names in print
            foodProvisionTypeId: first.feeding?.foodProvisionerTypeId ?? first.feeding?.foodProvisionTypeId,
            foodTypeId: first.feeding?.foodTypeId,
            quantity: {
                quantity: first.feeding?.foodQuantity ? parseFloat(first.feeding?.foodQuantity) : undefined,
                unit: first.feeding?.foodQuantityUnitId
            },
            brand: first.feeding?.feedingBrand
        };
    });

    // Medications: group by name/method/dosage
    const medicationTasks = tasks.filter(task => task.medication?.medicationName);

    const groupedMedications = getGroupedMedicationsFromTasks(medicationTasks, tz);

    const medicationGrouped = new Map<string, any[]>();
    medicationTasks.forEach(task => {
        const key = `${task.medication?.medicationName || ''}-${task.medication?.medicationMethodId || ''}-${
            task.medication?.dosageAmount || ''
        }-${task.medication?.dosageUnitId || ''}`;
        if (!medicationGrouped.has(key)) medicationGrouped.set(key, []);
        medicationGrouped.get(key)!.push(task);
    });

    const medications = Array.from(medicationGrouped.values()).map(group => {
        const first = group[0];

        // times observed on taskDate (fallback / daily schedule)
        const timesSet = new Set(
            group.map((t: any) => (t.taskDate ? formatTime(t.taskDate, tz) : undefined)).filter(Boolean) as string[]
        );
        const times = Array.from(timesSet) as string[];
        times.sort((a, b) => moment.tz(a, 'h:mm a', tz).diff(moment.tz(b, 'h:mm a', tz)));

        // Collect any specificSchedule entries attached to tasks or medication definition
        const specificSchedules: any[] = [];
        group.forEach((t: any) => {
            if (Array.isArray(t.medication?.specificSchedule) && t.medication.specificSchedule.length) {
                specificSchedules.push(...t.medication.specificSchedule);
            }
        });
        // also check first.medication (some models store schedule on the medication object)
        if (specificSchedules.length === 0 && Array.isArray(first.medication?.specificSchedule)) {
            specificSchedules.push(...first.medication.specificSchedule);
        }

        let schedule: any = undefined;
        // If we have explicit per-date schedules, normalise them to { date: 'YYYY-MM-DD', times: ['h:mm a'] }
        if (specificSchedules.length > 0) {
            schedule = { type: 'specific', doses: [] as any[] };
            // map entries to date -> times
            const byDate = new Map<string, Set<string>>();
            specificSchedules.forEach((entry: any) => {
                // entry.date may be a string like 'YYYY-MM-DD' or a Moment
                const dateKey = entry.date
                    ? moment.isMoment(entry.date)
                        ? entry.date.clone().tz(tz).format('YYYY-MM-DD')
                        : moment.tz(entry.date, 'YYYY-MM-DD', tz).format('YYYY-MM-DD')
                    : entry.dateKey || entry.day;

                const timesArr: string[] = [];

                // doses array may contain { time } or { taskDate }
                if (Array.isArray(entry.doses)) {
                    entry.doses.forEach((d: any) => {
                        if (d.time) {
                            // d.time may be a Moment or a string
                            if (moment.isMoment(d.time)) timesArr.push(formatTime(d.time, tz));
                            else timesArr.push(formatTime(moment.tz(d.time, tz), tz));
                        } else if (d.taskDate) {
                            timesArr.push(formatTime(d.taskDate, tz));
                        }
                    });
                }

                // some shapes use `times` instead of `doses`
                if (Array.isArray(entry.times)) {
                    entry.times.forEach((tt: any) => {
                        if (moment.isMoment(tt)) timesArr.push(formatTime(tt, tz));
                        else timesArr.push(formatTime(moment.tz(tt, tz), tz));
                    });
                }

                const set = byDate.get(dateKey) ?? new Set<string>();
                timesArr.filter(Boolean).forEach(t => set.add(t));
                byDate.set(dateKey, set);
            });

            Array.from(byDate.entries()).forEach(([date, set]) => {
                const t = Array.from(set.values());
                t.sort((a, b) => moment.tz(a, 'h:mm a', tz).diff(moment.tz(b, 'h:mm a', tz)));
                schedule.doses.push({ date, times: t });
            });
        } else if (first.medication?.schedule) {
            // Generic schedule object (e.g. daily or days-of-week)
            const s = first.medication.schedule;
            // normalise typical shapes: { type: 'daily' } or { daily: true } or { days: ['Mon','Tue'] }
            if (s.type === 'daily' || s.daily === true) {
                schedule = { type: 'daily', times };
            } else if (Array.isArray(s.days) && s.days.length) {
                schedule = { type: 'days', days: s.days, times };
            } else {
                // fallback: preserve raw schedule object
                schedule = { type: 'raw', raw: s, times };
            }
        }

        return {
            name: first.medication?.medicationName,
            methodType: first.medication?.medicationMethodId,
            dosage: {
                quantity: first.medication?.dosageAmount ? parseFloat(first.medication?.dosageAmount) : undefined,
                unit: first.medication?.dosageUnitId
            },
            instructions: first.medication?.medicationInstructions,
            // times observed on tasks (useful as fallback)
            times,
            // schedule (if present) will contain either { type: 'specific', doses: [{date,times}] }
            // or { type: 'daily'|'days'|'raw', ... }
            schedule
        };
    });

    return {
        id: String(item.orderItem?.id ?? item.appointment.id ?? item.orderItem?.uuid),

        businessAreaId,
        businessAreaName: svc?.businessArea?.name,

        serviceName: svc?.name ?? item.orderItem?.description,
        staffName: staff ? `${staff.firstName} ${staff.lastName}`.trim() : undefined,

        timeLabel: isBoarding ? undefined : formatTime(from, tz),
        rangeLabel:
            isBoarding && nightsCount !== undefined ? buildBoardingRangeLabel(from, to, nightsCount, tz) : undefined,

        fromLabel: isBoarding ? formatDateTime(from, tz) : undefined,
        toLabel: isBoarding ? formatDateTime(to, tz) : undefined,
        nightsCount: isBoarding ? nightsCount : undefined,

        raw: item,
        feedingSchedules,
        medications,
        groupedMedications
    } as PrintBookingDetailAppointmentViewModel;
};

const mapAddOn = (item: AddOnServiceOrderItem, tz: string): PrintBookingDetailAddOnViewModel => {
    const staff = item.staff;

    return {
        id: String(item.orderItem?.id ?? item.orderItem?.uuid ?? Math.random()),
        timeLabel: item.orderItem?.startedAt ? formatTime(addOnStart(item), tz) : '',
        name: item.addOnService?.name ?? item.orderItem?.description ?? 'Add-On',
        staffName: staff ? `${staff.firstName} ${staff.lastName}`.trim() : undefined,
        raw: item
    };
};

const collectDayKeys = (
    appointments: AppointmentOrderItem[],
    addOns: AddOnServiceOrderItem[],
    tz: string
): string[] => {
    const keys: string[] = [];

    appointments.forEach(a => keys.push(dayKeyFromMoment(appointmentFrom(a), tz)));
    addOns.filter(a => !!a.orderItem?.startedAt).forEach(a => keys.push(dayKeyFromMoment(addOnStart(a), tz)));

    return Array.from(new Set(keys)).sort();
};

/* ---------------- Mapper ---------------- */

export function groupBookingDetailToViewModel(order: Order, tz: string): PrintBookingDetailViewModel[] {
    const appointments = order.items?.appointments ?? [];
    const addOns = order.items?.addOnServices ?? [];

    const byPet = new Map<number, { appointments: AppointmentOrderItem[]; addOns: AddOnServiceOrderItem[] }>();

    const ensurePet = (petId: number) => {
        if (!byPet.has(petId)) byPet.set(petId, { appointments: [], addOns: [] });
        return byPet.get(petId)!;
    };

    appointments.forEach(item => {
        const petId = item.pet?.id ?? item.orderItem?.petId;
        if (petId) ensurePet(petId).appointments.push(item);
    });

    addOns.forEach(item => {
        const petId = item.pet?.id ?? item.orderItem?.petId;
        if (petId) ensurePet(petId).addOns.push(item);
    });

    const petSections: PrintBookingDetailViewModel[] = Array.from(byPet.values()).map(group => {
        const rawAppointments = group.appointments
            .slice()
            .sort((a, b) => sortByMomentAsc(appointmentFrom(a), appointmentFrom(b)));

        const rawAddOns = group.addOns
            .slice()
            .filter(a => !!a.orderItem?.startedAt)
            .sort((a, b) => sortByMomentAsc(addOnStart(a), addOnStart(b)));

        const pet = rawAppointments[0]?.pet ?? rawAddOns[0]?.pet;

        const dayKeys = collectDayKeys(rawAppointments, rawAddOns, tz);

        const groups: PrintBookingDetailGroupViewModel[] = dayKeys.map(key => ({
            dateKey: key,
            dateLabel: dayLabelFromKey(key, tz),
            appointments: [],
            addOns: []
        }));

        const groupMap = new Map(groups.map(g => [g.dateKey, g]));

        rawAppointments.forEach(item => {
            const key = dayKeyFromMoment(appointmentFrom(item), tz);
            groupMap.get(key)?.appointments.push(mapAppointment(item, tz));
        });

        rawAddOns.forEach(item => {
            const key = dayKeyFromMoment(addOnStart(item), tz);
            groupMap.get(key)?.addOns.push(mapAddOn(item, tz));
        });

        groups.forEach(g => {
            g.appointments.sort((a, b) =>
                sortByMomentAsc(a.raw.appointment.duration.from, b.raw.appointment.duration.from)
            );
            g.addOns.sort((a, b) =>
                sortByMomentAsc(
                    a.raw.orderItem.startedAt as unknown as moment.Moment,
                    b.raw.orderItem.startedAt as unknown as moment.Moment
                )
            );
        });

        // Merge transportation appointments that belong to the same date and are complementary (pickup + dropoff)
        groups.forEach(g => {
            const transports = g.appointments.filter(a => a.businessAreaId === 'transportation');
            if (transports.length <= 1) return;

            const others = g.appointments.filter(a => a.businessAreaId !== 'transportation');

            const used = new Set<number>();
            const mergedTransports: any[] = [];

            for (let i = 0; i < transports.length; i++) {
                if (used.has(i)) continue;
                const a = transports[i];
                const typeA = a.raw?.appointment?.details?.transportation?.type;

                // find complement with opposite type
                let foundIdx = -1;
                for (let j = i + 1; j < transports.length; j++) {
                    if (used.has(j)) continue;
                    const b = transports[j];
                    const typeB = b.raw?.appointment?.details?.transportation?.type;
                    if (typeA && typeB && typeA !== typeB) {
                        foundIdx = j;
                        break;
                    }
                }

                if (foundIdx !== -1) {
                    const b = transports[foundIdx];
                    used.add(i);
                    used.add(foundIdx);

                    // Build merged appointment: include both raw objects and pickup/dropoff details
                    const merged: any = { ...a };
                    merged.raw = { mergedPair: [b.raw, a.raw] };

                    const pickup =
                        typeA === 'pickup'
                            ? a.raw?.appointment?.details?.transportation
                            : b.raw?.appointment?.details?.transportation;
                    const dropoff =
                        typeA === 'dropoff'
                            ? a.raw?.appointment?.details?.transportation
                            : b.raw?.appointment?.details?.transportation;

                    merged.transportation = { pickup, dropoff };
                    // Prefer a.serviceName but fallback to b
                    merged.serviceName = a.serviceName ?? b.serviceName;

                    mergedTransports.push(merged);
                } else {
                    // no complement found, keep single transport
                    mergedTransports.push(a);
                    used.add(i);
                }
            }

            // Replace appointments for this group with others + merged transports (preserve order roughly)
            g.appointments = [...others, ...mergedTransports];
        });

        return {
            pet,
            rawAppointments,
            rawAddOns,
            groups
        };
    });

    petSections.sort((a, b) => {
        const aFirst = a.rawAppointments[0]?.appointment.duration.from ?? a.rawAddOns[0]?.orderItem?.startedAt;
        const bFirst = b.rawAppointments[0]?.appointment.duration.from ?? b.rawAddOns[0]?.orderItem?.startedAt;

        if (aFirst && bFirst) return sortByMomentAsc(aFirst, bFirst);
        return (a.pet?.name ?? '').localeCompare(b.pet?.name ?? '');
    });

    return petSections;
}
